import { mandateAuthorizationDetails } from '@pryme/protocol';
import { PrymeSdkError } from './errors.js';
const STATUS = {
    approval_required: 202, invalid_signature: 401, expired_presentation: 401, credential_revoked: 401, replay_detected: 409,
    rate_limited: 429, temporarily_unavailable: 503,
};
/**
 * Result of `pryme.verify(req)`. Getters are synchronous, local and network-free over
 * already-verified signed state. Anything transactional is `authorize()`, which is online.
 */
export class PrymeIdentity {
    kind;
    serviceId;
    error;
    lastDecision;
    init;
    constructor(init) {
        this.init = init;
        this.kind = init.kind;
        this.serviceId = init.serviceId;
        this.error = init.error;
    }
    /** Opaque, service-specific pairwise subject. Unrelated across services for the same human. */
    get subject() {
        return this.init.binding?.subject;
    }
    get environment() {
        return this.init.binding?.environment;
    }
    get expiresAt() {
        const exp = this.init.kind === 'agent' ? this.init.mandate?.exp : this.init.state?.exp;
        return exp ? new Date(exp * 1000) : undefined;
    }
    /** Persist this in your server-side session to keep `verify()` local while fresh. */
    get session() {
        if (!this.subject)
            return undefined;
        return { subject: this.subject, state: this.init.stateToken, binding: this.init.bindingToken };
    }
    get agentId() {
        return this.init.agent?.agentId;
    }
    get mandate() {
        return this.init.mandate;
    }
    /**
     * The verified mandate as RFC 9396 `authorization_details`, for policy engines that speak
     * OAuth. Static scope only: budgets, approvals and revocation still need `authorize()`.
     */
    authorizationDetails(opts = {}) {
        if (!this.isDelegated() || !this.init.mandate)
            return undefined;
        return mandateAuthorizationDetails(this.init.mandate, opts);
    }
    /**
     * Full trust chain, audience, request binding and validity checks passed: the evidence is Pryme's and
     * for you. It says nothing about freshness: a session's state may be past its expiry (`isStale()`),
     * and its claims then read `stale`. `verify()` refreshes stale state for you; if you call
     * `identityFromSession()` yourself, check `isStale()` too (security review I2: accepted, documented).
     */
    isGenuine() {
        return !this.error && this.kind !== 'none' && !!this.init.binding;
    }
    isDelegated() {
        return this.isGenuine() && this.kind === 'agent';
    }
    isStale() {
        const exp = this.expiresAt;
        return !exp || exp.getTime() <= this.init.now();
    }
    claims() {
        if (this.kind === 'agent')
            return { claims: this.init.mandate?.claims ?? {}, withheld: {} };
        return { claims: this.init.state?.claims ?? {}, withheld: this.init.state?.withheld ?? {} };
    }
    claim(name) {
        if (!this.isGenuine())
            return { status: 'rejected' };
        if (this.isStale())
            return { status: 'stale' };
        const { claims, withheld } = this.claims();
        const c = claims[name];
        if (!c) {
            const w = withheld[name];
            return { status: w === 'not_disclosed' || !w ? 'not_disclosed' : 'unavailable' };
        }
        // A production verifier never accepts sandbox evidence as real human verification (T26/T35).
        if (this.init.expectedEnvironment === 'production' && c.assurance.environment !== 'production') {
            return { status: 'rejected', assurance: c.assurance };
        }
        if (typeof c.value === 'boolean')
            return { status: c.value ? 'true' : 'false', value: c.value, assurance: c.assurance };
        return { status: 'disclosed', value: c.value, assurance: c.assurance };
    }
    claimStatus(name) {
        return this.claim(name).status;
    }
    /** Issuer-backed human verification (not merely possession of an agent key). */
    isHumanVerified() {
        return this.claimStatus('human.verified') === 'true';
    }
    isOver(age) {
        return this.claimStatus(`age.over${age}`) === 'true';
    }
    isOver18() {
        return this.isOver(18);
    }
    /**
     * @deprecated Removed (security review 2026-11, L11): Pryme has no evidence of where a person lives, so
     * it never asserts residence. Always throws, so a residence check can't silently pass or fail on other
     * evidence. Use `eidSchemeCountry()` if what you need is the country of the person's eID.
     */
    isResidentOf(_country) {
        throw new PrymeSdkError('invalid_request', 'residency is not a Pryme claim: use eidSchemeCountry() for the country of the eID the person verified with');
    }
    /**
     * The country of the eID scheme the person verified with (`DK` for MitID), if disclosed (`eid.scheme_country`).
     * Not nationality, not residence. Null when not disclosed (fails closed).
     */
    eidSchemeCountry() {
        const c = this.claim('eid.scheme_country');
        return c.status === 'disclosed' && typeof c.value === 'string' ? c.value : null;
    }
    /** Assurance descriptor of the human verification, for display/audit (never raw evidence). */
    assurance() {
        return this.claims().claims['human.verified']?.assurance;
    }
    /**
     * Online, transaction-specific mandate check. Returns true only for `allow`.
     * Inspect `lastDecision` for `approval_required` (with approvalId) or deny codes.
     * Forwards the agent's signed intent: Pryme uses the permission only for exactly the operation the
     * agent signed, once. Without one it can only ask the person (reason `requested_by_service`, ADR-0015).
     */
    async authorize(action, context = {}) {
        const d = await this.authorizeDetailed(action, context);
        return d.outcome === 'allow';
    }
    async authorizeDetailed(action, context = {}) {
        if (!this.isDelegated() || !this.init.authorizeFn) {
            this.lastDecision = { outcome: 'deny', code: 'agent_not_authorized', reason: 'request is not a verified delegated agent request' };
            return this.lastDecision;
        }
        try {
            this.lastDecision = await this.init.authorizeFn(action, context);
        }
        catch (e) {
            const code = e.code ?? 'temporarily_unavailable';
            // Fail closed when Pryme is unreachable or the response cannot be verified (T29).
            this.lastDecision = { outcome: 'deny', code, reason: e.message };
        }
        return this.lastDecision;
    }
    /** Machine-readable rejection. Pass an Express `res`, or omit to get a fetch `Response`. */
    reject(res) {
        const d = this.lastDecision;
        const code = this.error?.code ?? (d && d.outcome !== 'allow' ? d.code ?? 'approval_required' : 'identity_not_verified');
        const body = { error: { code, message: this.error?.message ?? d?.reason ?? code } };
        if (d?.outcome === 'approval_required') {
            body.approval = { id: d.approvalId, expiresAt: d.approvalExpiresAt, retryWithHeader: 'Pryme-Approval' };
        }
        return send(res, STATUS[code] ?? 403, body);
    }
    /** 403 with a human-readable message. `code` defaults to the verification error, else `forbidden`. */
    rejectWithMessage(res, message, code = this.error?.code ?? 'forbidden') {
        return send(res, 403, { error: { code, message } });
    }
    toJSON() {
        return {
            kind: this.kind,
            genuine: this.isGenuine(),
            subject: this.subject,
            environment: this.environment,
            expiresAt: this.expiresAt?.toISOString(),
            agentId: this.agentId,
            mandateId: this.mandate?.mandate_id,
            error: this.error,
        };
    }
}
function send(res, status, body) {
    if (res && typeof res.status === 'function') {
        return res.status(status).json(body);
    }
    return new Response(JSON.stringify(body), { status, headers: { 'content-type': 'application/json' } });
}
